import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { apiScoping } from '@/lib/api/scoping'
import { DEMO_ASSESSMENT_ID } from '@/features/demo/domain/demo-scope'
import { assessmentKeys } from '@/features/assessments/api/query-keys'

const HUBSPOT_URL_PATTERN = /app\.hubspot\.com\/contacts\/\d+\/record\/0-3\/(\d+)/

function extractHubspotDealId(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  const match = value.match(HUBSPOT_URL_PATTERN)
  if (match?.[1]) return match[1]
  return value
}

const ExternalReferenceSchema = z.object({
  externalSystem: z.string(),
  externalID: z.string()
})

export const PlantAssessmentSchema = z
  .object({
    id: z.string(),
    plantName: z.string().min(1),
    country: z.string().regex(/^[A-Z]{3}$/, 'Must be a valid ISO 3166-1 alpha-3 code'),
    status: z.enum(['pending', 'inProgress', 'completed']),
    // Scoping schema version (1 = legacy, 2 = ontology). Absent/null means 1.
    scopingSchemaVersion: z.number().nullish(),
    companyID: z.string().nullish(),
    dealOwnerUserID: z.string().nullish(),
    salesEngineerUserID: z.string().nullish(),
    isAtex: z.boolean().default(false),
    externalReferences: z
      .array(ExternalReferenceSchema)
      .nullish()
      .transform(refs => refs ?? []),
    createdAt: z.string(),
    updatedAt: z.string(),
    completedAt: z.string().nullish(),
    assessedAt: z.string().nullish(),
    lastOnsiteAt: z.string().nullish(),
    statusUpdatedAt: z.string().nullish(),
    createdByUserID: z.string(),
    updatedByUserID: z.string(),
    completedByUserID: z.string().nullish(),
    deletedAt: z.string().nullish(),
    deletedByUserID: z.string().nullish()
  })
  .transform(data => {
    const hubspotRef = data.externalReferences.find(ref => ref.externalSystem === 'hubspot')
    const hubspotPlantRef = data.externalReferences.find(
      ref => ref.externalSystem === 'hubspotPlant'
    )
    return {
      ...data,
      hubspotDealId: extractHubspotDealId(hubspotRef?.externalID),
      hubspotPlantId: hubspotPlantRef?.externalID
    }
  })

export type PlantAssessment = z.infer<typeof PlantAssessmentSchema>

export enum PlantAssessmentStatus {
  Pending = 'pending',
  InProgress = 'inProgress',
  Completed = 'completed'
}

const AssessmentListResponseSchema = z.object({
  data: z.array(PlantAssessmentSchema),
  count: z.number(),
  page: z.number(),
  limit: z.number()
})

export type AssessmentListResponse = z.infer<typeof AssessmentListResponseSchema>

export type AssessmentListParams = {
  id?: string
  page?: number
  limit?: number
  status?: string | string[]
  search?: string
  sortField?: string
  sortOrder?: 'asc' | 'desc'
  dealOwnerUserID?: string[]
  salesEngineerUserID?: string[]
  country?: string[]
  assessedFrom?: string
  assessedTo?: string
  updatedFrom?: string
  updatedTo?: string
  completedFrom?: string
  completedTo?: string
  scopedDevices?: string[]
  hasStarted?: boolean
  isSubmitted?: boolean
}

function serializeParams(params: AssessmentListParams): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue
    if (Array.isArray(v)) {
      if (v.length > 0) out[k] = v.join(',')
    } else {
      out[k] = v
    }
  }
  return out
}

export async function fetchAssessmentList(
  params: AssessmentListParams
): Promise<AssessmentListResponse> {
  // Demo resolution guard (ADR 005) — see assessment-asset-list for the full rationale.
  // Only fires for a single-assessment lookup by the demo id (how the demo header's
  // `usePlantAssessmentStatus` calls this), never for the general list, so list views
  // stay on the real path even in dev.
  if (import.meta.env.VITE_DEMO_USE_FIXTURES !== 'false' && params.id === DEMO_ASSESSMENT_ID) {
    return (await import('./fixtures/demo-assessment')).demoAssessmentListResponse
  }

  const res = await apiScoping.get('/api/v1/plant-assessment', { params: serializeParams(params) })
  const result = AssessmentListResponseSchema.safeParse(res.data)
  if (!result.success) {
    console.error(
      '[assessment-list] Zod parse failed - raw response:',
      res.data,
      'issues:',
      result.error.issues
    )
    throw result.error
  }
  return result.data
}

export function assessmentListQueryOptions(params: AssessmentListParams) {
  return queryOptions({
    queryKey: assessmentKeys.list(params),
    queryFn: () => fetchAssessmentList(params),
    staleTime: 60_000
  })
}
